import {
  Component,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  NgZone,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSliderModule } from '@angular/material/slider';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MapaFacilService } from './mapa-facil.service';
import {
  CircleConfig,
  PolygonMapa,
  LineStringMapa,
  Coordinate,
  TipoDatosPopup,
} from './interfaces';
import { PopupGenericoComponent } from './popup-generico/popup-generico.component';
import mapboxgl from 'mapbox-gl';
import { Subscription, fromEvent } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
} from 'rxjs/operators';
import { MapaFacilData } from './interfaces';
import { AuxiliaresModule } from '../auxiliares.module';
import {
  IMapaConfig,
  TipoMapaBase,
} from '../../modulos/standalone/selector-tipo-mapa/selector-tipo-mapa.component';
import { ObtenerDataEntidadService } from './obtener-data-entidad.component';
import { TooltipDirective } from '../tooltip/tooltip.directive';
import { MapboxService } from '../mapbox/mapbox.service';
import { MapboxLayersService } from '../mapbox/mapbox-layers.service';
import { MapboxGeocodingService } from '../mapbox/mapbox-geocoding.service';
import { MapboxDrawService } from '../mapbox/mapbox-draw.service';
import { MAPBOX_TOKEN } from '../../../environments/environment';
import { HelperService } from '../servicios/helper.service';
import { aplicarEtiquetasMapa } from '../mapbox/etiquetas-mapa';

@Component({
  standalone: true,
  selector: 'app-mapa-facil-view',
  templateUrl: './mapa-facil.component.html',
  styleUrl: './mapa-facil.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatSliderModule,
    MatCardModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    
    AuxiliaresModule,
    PopupGenericoComponent,
    TooltipDirective,
  ],
})
export class MapaFacilViewComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  mapaService = inject(MapaFacilService);
  private cdr = inject(ChangeDetectorRef);
  dataEntidadService = inject(ObtenerDataEntidadService);
  private mapboxService = inject(MapboxService);
  private mapboxLayersService = inject(MapboxLayersService);
  private mapboxGeocodingService = inject(MapboxGeocodingService);
  private mapboxDrawService = inject(MapboxDrawService);
  private helper = inject(HelperService);
  private ngZone = inject(NgZone);

  @ViewChild('inputDireccion') inputDireccion?: ElementRef;

  public nombreMap = 'mapa-facil-' + Date.now();
  private subscription?: Subscription;

  // Propiedades para el template mapa-facil.component.html
  public data?: MapaFacilData;

  public hayVehiculos = false;
  public hayColectivos = false;
  public hayTrackers = false;
  public hayActivos = false;
  public hayLuminarias = false;
  public hayAlarmas = false;
  public haySirenas = false;
  public hayGateways = false;

  public hayParadas = false;
  public hayPuntosActivos = false;
  public hayPolylines = false;
  public hayHeatmaps = false;
  public selectorBaseLayer = false;

  // Contadores de markers por tipo
  public contadorVehiculos = 0;
  public contadorColectivos = 0;
  public contadorTrackers = 0;
  public contadorActivos = 0;
  public contadorLuminarias = 0;
  public contadorAlarmas = 0;
  public contadorSirenas = 0;

  // Propiedades para el popup flotante
  public popupPosition = { x: 0, y: 0 };
  private popupTimeout?: number;

  // Estados de habilitación para cada tipo de marker
  public vehiculosHabilitados = true;
  public colectivosHabilitados = true;
  public trackersHabilitados = true;
  public activosHabilitados = true;
  public luminariasHabilitadas = true;
  public alarmasHabilitadas = true;
  public sirenasHabilitadas = true;
  public gatewaysHabilitados = true;

  // Estados de clustering para cada tipo de marker
  public vehiculosClusterEnabled = false;
  public colectivosClusterEnabled = false;
  public trackersClusterEnabled = false;
  public activosClusterEnabled = false;
  public luminariasClusterEnabled = false;
  public alarmasClusterEnabled = false;
  public sirenasClusterEnabled = false;

  // Debounce timers
  private clusterUpdateTimer: any;
  private mapStateTimer: any;

  // Mapeo de tipos a iconos según implementación original de OpenLayers
  private readonly TYPE_TO_ICON_MAP: Record<string, string | any> = {
    vehiculos: 'vehiculo-icon',
    colectivos: 'bus-icon',
    trackers: 'tracker-icon',
    activos: 'pin-activo-icon',
    // Luminarias usa expresión dinámica según estado (se agrega en addClusterLayers)
    luminarias: null, // Se manejará con expresión
    alarmas: 'alarma-casa-icon',
    sirenas: null, // Se manejará con expresión según estado
    gateways: null, // Se manejará con expresión según estadoSalud
  };

  // Tipos que soportan clustering (tienen DOS sources: normal y clustered)
  private readonly TYPES_WITH_CLUSTERING = [
    'vehiculos',
    'colectivos',
    'trackers',
    'activos',
    'luminarias',
    'alarmas',
    'sirenas',
  ];

  // Mapbox map instance
  private map?: mapboxgl.Map;

  // Animation properties (ahora delegadas al servicio de Mapbox)
  private animating = false;
  private animationFrameId?: number;

  // Delegamos speed y currentTrip al servicio
  public get speed(): number {
    return this.mapaService.tripSpeed;
  }
  public set speed(value: number) {
    this.mapaService.tripSpeed = value;
  }

  public get currentTrip(): boolean {
    return this.mapaService.tripAnimating;
  }

  public get tripPaused(): boolean {
    return this.mapaService.tripPaused;
  }

  public tipoMapaBase = 'osm';

  // Mapas disponibles
  public mapasDisponibles: IMapaConfig[] = [];

  ngOnInit() {
    // Inicializar mapas disponibles con los mapas base estándar
    this.mapasDisponibles = [
      {
        key: 'osm',
        nombre: 'Mapbox Standard',
        icono: 'assets/icons/osm-icon.svg',
      },
      {
        key: 'satelite',
        nombre: 'Vista Satelital',
        icono: 'assets/icons/satellite_view.png',
      },
      {
        key: 'cartodb',
        nombre: 'Minimal',
        icono: 'assets/icons/cartodb.webp',
      },
    ];

    // Agregar mapas personalizados que ya están en capasBase del servicio
    Object.keys(this.mapaService.capasBase).forEach((key) => {
      if (key.startsWith('personalizado_')) {
        const index = parseInt(key.replace('personalizado_', ''));
        const layer = this.mapaService.mapasPersonalizados?.[index];
        if (layer) {
          this.mapasDisponibles.push({
            key: key,
            nombre: layer.nombre || `Personalizado ${index + 1}`,
            icono: layer.icono,
          });
        }
      }
    });

    // Resetear flags
    this.hayVehiculos = false;
    this.hayColectivos = false;
    this.hayTrackers = false;
    this.hayLuminarias = false;
    this.hayGateways = false;
    this.hayAlarmas = false;
    this.hayActivos = false;
    this.haySirenas = false;
    this.hayParadas = false;
    this.hayPuntosActivos = false;
    this.hayPolylines = false;
    this.hayHeatmaps = false;
    this.selectorBaseLayer = false;

    // Contadores
    this.contadorVehiculos = 0;
    this.contadorColectivos = 0;
    this.contadorTrackers = 0;
    this.contadorActivos = 0;
    this.contadorLuminarias = 0;
    this.contadorAlarmas = 0;
    this.contadorSirenas = 0;

    // Suscripción a los datos del mapa
    this.subscription = this.mapaService.data.subscribe({
      next: (data) => {
        if (data) {
          this.updateMapData(data);
        }
      },
    });

    // Escuchar eventos de configuración del mapa
    window.addEventListener('mapa-3d-toggle', this.handleMapa3DToggle);
    window.addEventListener(
      'mapa-dia-noche-toggle',
      this.handleMapaDiaNocheToggle,
    );
  }

  private handleMapa3DToggle = (e: any) => {
    if (!this.map) return;
    try {
      this.map.setConfigProperty('basemap', 'show3dObjects', e.detail);
    } catch (error) {
      console.warn('⚠️ No se pudo actualizar 3D objects:', error);
    }
  };

  private handleMapaDiaNocheToggle = (e: any) => {
    if (!this.map) return;
    try {
      const lightPreset = e.detail ? this.calcularLightPreset() : 'day';
      this.map.setConfigProperty('basemap', 'lightPreset', lightPreset);
    } catch (error) {
      console.warn('⚠️ No se pudo actualizar lightPreset:', error);
    }
  };

  async ngAfterViewInit() {
    // Esperamos a que el DOM esté listo
    await this.initMap();

    // Setup address search input listener
    this.setupAddressSearchListener();
  }

  ngOnDestroy() {
    // Limpiar event listeners de configuración
    window.removeEventListener('mapa-3d-toggle', this.handleMapa3DToggle);
    window.removeEventListener(
      'mapa-dia-noche-toggle',
      this.handleMapaDiaNocheToggle,
    );

    // Parar animaciones
    if (this.animating) this.stopAnimation();

    // Limpiar timers
    if (this.clusterUpdateTimer) {
      clearTimeout(this.clusterUpdateTimer);
    }
    if (this.mapStateTimer) {
      clearTimeout(this.mapStateTimer);
    }

    // Limpiar subscripciones
    this.subscription?.unsubscribe();

    // CRÍTICO: Resetear referencia del mapa en el servicio singleton.
    // MapaFacilService es providedIn:'root'. Si no se resetea, el polling
    // de MapaFacilComponent.ngOnInit() detecta el mapa viejo y dispara
    // `inicializado` antes de que el nuevo mapa esté listo.
    // NO llamamos map.remove() aquí — initMap() ya tiene lógica para
    // detectar y eliminar el mapa viejo via mapboxService.getMap().
    this.mapaService.map = undefined;

    // Limpiar el estado del popup
    this.mapaService.popupVisible = false;
    this.mapaService.popupDatos = null;
    this.mapaService.popupTitulo = undefined;
    this.mapaService.popupCoordinate = null;
    this.updateVisibilityFlags({});

    // NUEVO: Limpiar controles de edición (MapboxDraw)
    // Esto soluciona el bug de controles de edición persistentes al navegar entre vistas
    try {
      this.mapboxDrawService.cleanup();
    } catch (error) {
      console.error('❌ Error limpiando MapboxDrawService:', error);
    }

    // TODO: En el futuro, aquí también limpiaremos el EntityManagerRegistry
    // cuando migremos completamente a la nueva arquitectura de managers
    // Ejemplo:
    // this.entityManagerRegistry.cleanupAll();
  }

  public layerToggle(layer: string) {
    if (!this.map) return;

    // Caso especial para trip (maneja múltiples layers)
    if (layer === 'trip') {
      this.mapaService.toggleTripVisibility();
      return;
    }

    // Mapbox toggle visibility
    const layerId =
      this.mapboxService.LAYER_IDS[
        layer as keyof typeof this.mapboxService.LAYER_IDS
      ];
    if (layerId && this.map.getLayer(layerId)) {
      const currentVisibility = this.map.getLayoutProperty(
        layerId,
        'visibility',
      );
      const newVisibility =
        currentVisibility === 'visible' ? 'none' : 'visible';
      this.map.setLayoutProperty(layerId, 'visibility', newVisibility);
    }
  }

  private updateVisibilityFlags(data: MapaFacilData) {
    if (data.markers?.some((m) => m.label === 'vehiculo')) {
      this.hayVehiculos = true;
      this.contadorVehiculos = data.markers?.filter(
        (m) => m.label === 'vehiculo',
      ).length;
    }
    if (data.markers?.some((m) => m.label === 'colectivo')) {
      this.hayColectivos = true;
      this.contadorColectivos = data.markers?.filter(
        (m) => m.label === 'colectivo',
      ).length;
    }
    if (data.markers?.some((m) => m.label === 'tracker')) {
      this.hayTrackers = true;
      this.contadorTrackers = data.markers?.filter(
        (m) => m.label === 'tracker',
      ).length;
    }
    if (data.markers?.some((m) => m.label === 'activo')) {
      this.hayActivos = true;
      this.contadorActivos = data.markers?.filter(
        (m) => m.label === 'activo',
      ).length;
    }
    if (data.markers?.some((m) => m.label === 'luminaria')) {
      this.hayLuminarias = true;
      this.contadorLuminarias = data.markers?.filter(
        (m) => m.label === 'luminaria',
      ).length;
    }
    if (data.markers?.some((m) => m.label === 'gateway')) {
      this.hayGateways = true;
    }
    if (data.markers?.some((m) => m.label === 'alarma')) {
      this.hayAlarmas = true;
      this.contadorAlarmas = data.markers?.filter(
        (m) => m.label === 'alarma',
      ).length;
    }
    if (data.markers?.some((m) => m.label === 'sirena')) {
      this.haySirenas = true;
      this.contadorSirenas = data.markers?.filter(
        (m) => m.label === 'sirena',
      ).length;
    }

    this.hayParadas = data.markers?.some((m) => m.label === 'parada') || false;
    this.hayPolylines = (data.polylines && data.polylines.length > 0) || false;
    this.hayHeatmaps = (data.heatmaps && data.heatmaps.length > 0) || false;
    this.selectorBaseLayer = data.selectorBaseLayer === false ? false : true;

    // Update clustering flags from config
    this.vehiculosClusterEnabled =
      this.mapaService.clusteringConfig.vehiculos?.enabled === false
        ? false
        : true;
    this.colectivosClusterEnabled =
      this.mapaService.clusteringConfig.colectivos?.enabled === false
        ? false
        : true;
    this.trackersClusterEnabled =
      this.mapaService.clusteringConfig.trackers?.enabled === false
        ? false
        : true;
    this.activosClusterEnabled =
      this.mapaService.clusteringConfig.activos?.enabled === false
        ? false
        : true;
    this.luminariasClusterEnabled =
      this.mapaService.clusteringConfig.luminarias?.enabled === false
        ? false
        : true;
    this.alarmasClusterEnabled =
      this.mapaService.clusteringConfig.alarmas?.enabled === false
        ? false
        : true;
    this.sirenasClusterEnabled =
      this.mapaService.clusteringConfig.sirenas?.enabled === false
        ? false
        : true;
  }

  private async initMap() {
    // SOLUCIÓN AL PROBLEMA DE NAVEGACIÓN:
    // Verificar que el contenedor existe en el DOM antes de continuar
    const container = document.getElementById(this.nombreMap);
    if (!container) {
      // Intentar de nuevo después de un frame
      requestAnimationFrame(() => this.initMap());
      return;
    }

    // Check if map already exists in the singleton service
    const existingMap = this.mapboxService.getMap();
    if (existingMap) {
      try {
        // Verificar que el mapa no esté ya en este contenedor
        const currentContainer = (existingMap as any).getContainer();

        // Caso 1: El mapa ya está en el contenedor correcto → reutilizar
        if (currentContainer && currentContainer.id === this.nombreMap) {
          this.map = existingMap;
          // IMPORTANTE: Notificar al servicio incluso cuando se reutiliza
          this.mapaService.setMap(existingMap);
          return;
        }

        // Caso 2: El mapa está en un contenedor diferente
        // Verificar si el contenedor anterior todavía existe en el DOM
        const oldContainerExists =
          currentContainer && document.getElementById(currentContainer.id);

        if (!oldContainerExists) {
          // El contenedor anterior fue destruido (componente destruido)
          // IMPORTANTE: Debemos destruir el mapa viejo antes de crear uno nuevo
          // para evitar memory leaks y estados inconsistentes
          existingMap.remove();
          this.mapboxService.setMap(null as any);
          // Continuar para crear un nuevo mapa abajo
        } else {
          // El contenedor anterior aún existe pero es diferente al actual
          // Mapbox GL JS NO soporta mover mapas entre contenedores
          // Debemos destruir el mapa viejo y crear uno nuevo
          existingMap.remove();
          this.mapboxService.setMap(null as any);
          // Continuar para crear un nuevo mapa abajo
        }
      } catch (error) {
        console.error('❌ Error al verificar/reutilizar mapa:', error);
        // Si falla, intentar crear uno nuevo eliminando el mapa existente
        try {
          if (existingMap) existingMap.remove();
        } catch (e) {
          console.warn('⚠️ Error al destruir mapa en catch:', e);
        }
        this.mapboxService.setMap(null as any);
      }
    }

    // Get saved state or use defaults
    const savedCenter = this.loadMapCenter();
    const savedZoom = this.loadMapZoom();
    const baseStyle = this.getMapboxStyleUrl(this.tipoMapaBase);

    // CRÍTICO: Crear mapa FUERA de Angular Zone para evitar que Mapbox dispare change detection
    // El loop de renderizado interno de Mapbox usa requestAnimationFrame, MessageChannel y Promises
    // que quedarían patcheados por Zone.js si se crean dentro de Angular Zone
    let newMap: mapboxgl.Map;
    this.ngZone.runOutsideAngular(() => {
      // Create new Mapbox map
      newMap = new mapboxgl.Map({
        language: 'es', // Etiquetas del mapa base en español
        container: this.nombreMap,
        style: baseStyle,
        center: savedCenter,
        zoom: savedZoom,
        maxZoom: 19,
        accessToken: MAPBOX_TOKEN,
        // Requiere Ctrl/Cmd + scroll para zoom solo si está habilitado en el servicio
        // Útil para mapas en formularios/detalles, NO para mapas fullscreen
        cooperativeGestures: this.mapaService.cooperativeGestures,
      });
      aplicarEtiquetasMapa(newMap);

      // Agregar controles de navegación
      newMap.addControl(
        new mapboxgl.NavigationControl({
          showCompass: true,
          showZoom: true,
          visualizePitch: true,
        }),
        'bottom-right',
      );

      // Event when map loads
      newMap.on('load', async () => {
        await this.onMapLoaded(newMap);
      });

      // IMPORTANTE: Para Mapbox Standard, usar style.load para configuraciones
      newMap.on('style.load', () => {
        this.configurarCicloDiaNoche(newMap);
      });

      // Setup map state tracking
      newMap.on('moveend', () => {
        this.saveMapState();
      });
    });

    this.map = newMap!;
    this.mapboxService.setMap(newMap!);
  }

  private async onMapLoaded(map: mapboxgl.Map) {
    // Load all icons
    await this.mapboxLayersService.loadAllIcons(map);

    // Initialize all layers
    await this.initializeLayers(map);

    // Initialize MapboxDraw for editing (if editing is enabled)
    if (this.mapaService.editingConfig?.enabled) {
      this.mapboxDrawService.initDrawControls(map);
    }

    // CRÍTICO: Marcar el mapa como listo DESPUÉS de inicializar MapboxDraw
    // para que las geometrías de edición pendientes puedan cargarse correctamente
    this.mapboxService.markMapAsReady();

    // Setup event handlers
    this.setupEventHandlers(map);

    // CRÍTICO: Notificar al servicio que el mapa está listo
    // Esto es necesario para que el evento (inicializado) se emita
    // y los componentes padres puedan configurar clustering, etc.
    this.mapaService.setMap(map);
  }

  private async initializeLayers(map: mapboxgl.Map) {
    // IMPORTANTE: Leer configuración de clustering ANTES de crear sources
    // Según OpenLayers original: Por defecto DESHABILITADO, excepto alarmas/sirenas
    const clusterConfig = this.mapaService.clusteringConfig || {};
    const defaultDistance = 40; // Según implementación original OpenLayers

    // PASO 1: Crear todos los GeoJSON sources vacíos
    const emptyGeoJSON: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: [],
    };

    // ⚠️ IMPORTANTE: En Mapbox, el clustering se hace a nivel de SOURCE, no de layer
    // Por eso necesitamos DOS sources por tipo:
    // - source normal (sin clustering) para cuando el toggle está OFF
    // - source clustered (con clustering) para cuando el toggle está ON
    // Las capas unclustered apuntan al source normal
    // Las capas cluster apuntan al source clustered
    // Cuando actualizamos datos, los cargamos en AMBOS sources

    this.TYPES_WITH_CLUSTERING.forEach((type) => {
      const sourceId = (this.mapboxService.SOURCE_IDS as any)[type];
      const clusteredSourceId = `${sourceId}-clustered`;

      // Source SIN clustering (para modo unclustered)
      map.addSource(sourceId, {
        type: 'geojson',
        data: emptyGeoJSON,
      });

      // Source CON clustering (para modo clustered)
      map.addSource(clusteredSourceId, {
        type: 'geojson',
        data: emptyGeoJSON,
        cluster: true,
        clusterRadius:
          (clusterConfig as any)[type]?.distance || defaultDistance,
        clusterMaxZoom: 14,
      });
    });

    map.addSource(this.mapboxService.SOURCE_IDS.gateways, {
      type: 'geojson',
      data: emptyGeoJSON,
    });

    // Sources para emergencias
    map.addSource(this.mapboxService.SOURCE_IDS.emergenciasMedicas, {
      type: 'geojson',
      data: emptyGeoJSON,
    });

    map.addSource(this.mapboxService.SOURCE_IDS.ambulancias, {
      type: 'geojson',
      data: emptyGeoJSON,
    });

    map.addSource(this.mapboxService.SOURCE_IDS.hospitales, {
      type: 'geojson',
      data: emptyGeoJSON,
    });

    map.addSource(this.mapboxService.SOURCE_IDS.centrosMedicos, {
      type: 'geojson',
      data: emptyGeoJSON,
    });

    // Sources para geometrías
    map.addSource(this.mapboxService.SOURCE_IDS.paradas, {
      type: 'geojson',
      data: emptyGeoJSON,
    });

    map.addSource(this.mapboxService.SOURCE_IDS.polylines, {
      type: 'geojson',
      data: emptyGeoJSON,
    });

    map.addSource(this.mapboxService.SOURCE_IDS.polygons, {
      type: 'geojson',
      data: emptyGeoJSON,
    });

    map.addSource(this.mapboxService.SOURCE_IDS.circles, {
      type: 'geojson',
      data: emptyGeoJSON,
    });

    map.addSource(this.mapboxService.SOURCE_IDS.heatmap, {
      type: 'geojson',
      data: emptyGeoJSON,
    });

    map.addSource(this.mapboxService.SOURCE_IDS.marker, {
      type: 'geojson',
      data: emptyGeoJSON,
    });

    // PASO 2: Crear layers (en orden correcto de z-index)

    // 1. Polígonos (fondo)
    map.addLayer({
      id: this.mapboxService.LAYER_IDS.polygons,
      type: 'fill',
      source: this.mapboxService.SOURCE_IDS.polygons,
      paint: {
        'fill-color': ['get', 'fillColor'],
        'fill-opacity': 0.3,
      },
    });

    map.addLayer({
      id: this.mapboxService.LAYER_IDS.polygonsOutline,
      type: 'line',
      source: this.mapboxService.SOURCE_IDS.polygons,
      paint: {
        'line-color': ['get', 'color'],
        'line-width': ['get', 'width'],
      },
    });

    // 2. Círculos
    map.addLayer({
      id: this.mapboxService.LAYER_IDS.circles,
      type: 'fill',
      source: this.mapboxService.SOURCE_IDS.circles,
      paint: {
        'fill-color': ['get', 'fillColor'],
        'fill-opacity': 0.3,
      },
    });

    map.addLayer({
      id: this.mapboxService.LAYER_IDS.circlesOutline,
      type: 'line',
      source: this.mapboxService.SOURCE_IDS.circles,
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 2,
      },
    });

    // 3. Polylines
    map.addLayer({
      id: this.mapboxService.LAYER_IDS.polylines,
      type: 'line',
      source: this.mapboxService.SOURCE_IDS.polylines,
      paint: {
        'line-color': ['get', 'color'],
        'line-width': ['get', 'width'],
        'line-opacity': ['get', 'opacity'],
      },
    });

    // 4. Heatmap
    map.addLayer({
      id: this.mapboxService.LAYER_IDS.heatmap,
      type: 'heatmap',
      source: this.mapboxService.SOURCE_IDS.heatmap,
      paint: {
        'heatmap-weight': 1,
        'heatmap-intensity': 1,
        'heatmap-radius': 20,
        'heatmap-opacity': 0.8,
        'heatmap-color': [
          'interpolate',
          ['linear'],
          ['heatmap-density'],
          0,
          'rgba(0,0,255,0)',
          0.2,
          'rgb(0,255,255)',
          0.4,
          'rgb(0,255,0)',
          0.6,
          'rgb(255,255,0)',
          0.8,
          'rgb(255,165,0)',
          1,
          'rgb(255,0,0)',
        ],
      },
    });

    // 5. Capas de markers (en orden correcto de z-index)
    // ORDEN: circle-far (zoom lejano) → background (circle/compass) → unclustered (icons) → clusters

    // Luminarias cambian a círculos en zoom lejano SOLO en el mapa específico de luminarias
    // En el mapa general usan pin fijo sin cambios por zoom
    const isMapaLuminarias =
      this.mapaService.nombreMap === 'MapaLuminariasComponent';
    const typesWithDynamicScale: string[] = isMapaLuminarias
      ? ['luminarias']
      : [];

    // 5a. Circle-far layers (zoom lejano < 14) - solo si es mapa de luminarias
    typesWithDynamicScale.forEach((type) =>
      this.addCircleLayerForFarZoom(
        map,
        type,
        (this.mapboxService.SOURCE_IDS as any)[type],
        this.getClusterColor(type),
      ),
    );

    // 5b. Background layers (circle/compass) - SOLO para tipos con orientación
    // IMPORTANTE: Alarmas NO usa sistema de dos capas en OpenLayers original
    ['vehiculos', 'colectivos', 'trackers'].forEach((type) =>
      this.addBackgroundLayer(map, type),
    );

    // 5c. Unclustered icon layers - iconos específicos
    this.TYPES_WITH_CLUSTERING.forEach((type) =>
      this.addUnclusteredLayer(map, type),
    );

    // 5d. Cluster layers - arriba de todo (círculos + números)
    this.TYPES_WITH_CLUSTERING.forEach((type) =>
      this.addClusterCircleAndCount(map, type),
    );

    // 6. Markers individuales (sin cluster)
    this.addMarkerLayer(map, 'gateways', 'gateway-icon');
    this.addMarkerLayer(map, 'emergenciasMedicas', 'emergencia-medica-icon');
    this.addMarkerLayer(map, 'ambulancias', 'ambulancia-icon');
    this.addMarkerLayer(map, 'hospitales', 'hospital-icon');
    this.addMarkerLayer(map, 'centrosMedicos', 'centro-medico-icon');
    this.addMarkerLayer(map, 'paradas', 'parada-icon');
    this.addMarkerLayer(map, 'marker', 'marker-icon');

    // NOTA: NO marcar el mapa como listo aquí
    // markMapAsReady() se llamará DESPUÉS de inicializar MapboxDraw
    // para que las geometrías de edición puedan cargarse correctamente
  }

  /**
   * Helper para obtener el color de cluster según el tipo
   * IMPORTANTE: Mapbox no soporta hex de 8 dígitos, usamos RGB puro + circle-opacity
   */
  private getClusterColor(type: string): string {
    const clusterColors: Record<string, string> = {
      vehiculos: '#2195f3', // Azul
      colectivos: '#ff7300', // Naranja
      trackers: '#9C27B0', // Púrpura
      activos: '#4CAF50', // Verde
      luminarias: '#fad66a', // Amarillo
      alarmas: '#F44336', // Rojo
      sirenas: '#d898b2', // Rosado
      gateways: '#607D8B', // Gris azulado
    };
    return clusterColors[type] || '#757575';
  }

  /**
   * Agrega solo los círculos y números de clusters
   * IMPORTANTE: Las capas de clusters apuntan al source CLUSTERED
   */
  private addClusterCircleAndCount(map: mapboxgl.Map, type: string) {
    const sourceId = (this.mapboxService.SOURCE_IDS as any)[type];
    const clusteredSourceId = `${sourceId}-clustered`; // Source con clustering habilitado
    const clusterColor = this.getClusterColor(type);

    // Cluster circles con colores específicos
    map.addLayer({
      id: `${type}-cluster`,
      type: 'circle',
      source: clusteredSourceId, // Apuntar al source CON clustering
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': clusterColor,
        'circle-opacity': 0.63, // ~a0 en hex = 160/255 = 0.627 (redondeado a 0.63)
        // Radios según cantidad (de OpenLayers original)
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          12, // 0-2 markers
          3,
          14, // 3-5 markers
          6,
          16, // 6-10 markers
          11,
          18, // 11-20 markers
          21,
          20, // 20+ markers
        ],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    });

    // Cluster count
    map.addLayer({
      id: `${type}-cluster-count`,
      type: 'symbol',
      source: clusteredSourceId, // Apuntar al source CON clustering
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-size': 12,
      },
    });
  }

  /**
   * Agrega la capa de iconos unclustered (markers individuales)
   */
  private addUnclusteredLayer(map: mapboxgl.Map, type: string) {
    const sourceId = (this.mapboxService.SOURCE_IDS as any)[type];
    const iconExpression = this.getIconExpressionForType(type);
    const iconSizeExpression = this.getIconSizeExpression(type);

    // Tipos que cambian a círculos en zoom lejano
    // SOLO en el mapa específico de luminarias
    const isMapaLuminarias =
      this.mapaService.nombreMap === 'MapaLuminariasComponent';
    const hasCircleFallback = type === 'luminarias' && isMapaLuminarias;

    const layerConfig: any = {
      id: `${type}-unclustered`,
      type: 'symbol',
      source: sourceId,
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': iconExpression,
        'icon-size': iconSizeExpression,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true, // Cambié a true para evitar culling
      },
    };

    // Luminarias: anchor en bottom (según OpenLayers usa anchor: [0.5, 1])
    if (type === 'luminarias') {
      layerConfig.layout['icon-anchor'] = 'bottom';
    }

    // SOLO luminarias en el mapa específico tienen minzoom (porque usan circle-far en zoom < 14)
    // En el mapa general y otros tipos se ven en TODOS los zooms
    if (hasCircleFallback) {
      layerConfig.minzoom = 14;
    }

    map.addLayer(layerConfig);
  }

  /**
   * Agrega layer de círculos para zoom lejano (<=12)
   * Replica comportamiento de OpenLayers donde en zoom lejano se mostraban CircleStyle
   */
  private addCircleLayerForFarZoom(
    map: mapboxgl.Map,
    type: string,
    sourceId: string,
    defaultColor: string,
  ) {
    // Colores específicos por estado para luminarias (según OpenLayers original)
    let circleColorExpression: any;

    if (type === 'luminarias') {
      // Para luminarias, color según estado
      circleColorExpression = [
        'match',
        ['get', 'estado'],
        'Alarma',
        'rgb(233, 0, 0)', // Rojo
        'Error de comunicacion',
        'rgb(223,94,8)', // Naranja
        'Error de encendido',
        'rgb(0, 0, 0)', // Negro
        'Apagada',
        'rgba(0, 0, 0, 1)', // Negro
        'Ok',
        'rgb(11,223,11)', // Verde
        'Encendida',
        'rgb(11,223,11)', // Verde (igual que Ok)
        'rgb(11,223,11)', // Default verde (funcionamiento correcto)
      ];
    } else {
      // Para otros tipos, usar color por defecto (mismo que clusters)
      circleColorExpression = defaultColor;
    }

    map.addLayer({
      id: `${type}-circle-far`,
      type: 'circle',
      source: sourceId,
      filter: ['!', ['has', 'point_count']], // Solo markers individuales (no clusters)
      paint: {
        'circle-radius': 4, // Tamaño fijo de círculo (según OpenLayers original)
        'circle-color': circleColorExpression,
        'circle-stroke-color': circleColorExpression,
        'circle-stroke-width': 1,
      },
      maxzoom: 14, // Solo mostrar en zoom < 14
    });
  }

  /**
   * Agrega la capa de fondo (circle/compass) para tipos con orientación
   * Replica el sistema de dos capas de OpenLayers:
   * - Background layer: circle.png (40x40) O brujula.png rotada si tiene orientation
   * - Foreground layer: icon específico (vehiculo.png 18x18, bus.png 40x40, tracker.png 20x20)
   * IMPORTANTE: circle.png/brujula.png son 800x800 forzados a 40x40 → 0.05
   */
  private addBackgroundLayer(map: mapboxgl.Map, type: string) {
    const sourceId = (this.mapboxService.SOURCE_IDS as any)[type];

    // Layer de fondo con circle O compass (depende de si tiene orientation)
    map.addLayer({
      id: `${type}-background`,
      type: 'symbol',
      source: sourceId,
      filter: ['!', ['has', 'point_count']], // Solo puntos no clusterizados
      layout: {
        // Icono: brujula.png si tiene orientation, circle.png si no
        'icon-image': [
          'case',
          ['has', 'orientation'],
          'compass-icon', // Brujula si tiene orientation
          'circle-icon', // Circle si no
        ],
        'icon-size': 0.05, // 800 * 0.05 = 40px (circle.png y brujula.png son 800x800)
        'icon-rotation-alignment': 'map',
        'icon-rotate': [
          'case',
          ['has', 'orientation'],
          ['get', 'orientation'], // Rotar según orientation
          0, // Sin rotación si no tiene
        ],
        'icon-allow-overlap': true,
        'icon-ignore-placement': true, // Evitar culling
      },
      // NO minzoom - en OpenLayers se ve a todos los niveles de zoom
    });
  }

  /**
   * Agrega background layer estático (solo circle.png, sin rotación)
   * Para tipos que no tienen orientación pero sí usan el sistema de dos capas
   * IMPORTANTE: circle.png es 800x800 forzado a 40x40 → 0.05
   */
  private addBackgroundLayerStatic(map: mapboxgl.Map, type: string) {
    const sourceId = (this.mapboxService.SOURCE_IDS as any)[type];

    map.addLayer({
      id: `${type}-background`,
      type: 'symbol',
      source: sourceId,
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': 'circle-icon', // circle.png (800x800)
        'icon-size': 0.05, // 800 * 0.05 = 40px (igual a OpenLayers)
        'icon-allow-overlap': true,
        'icon-ignore-placement': true, // Evitar culling
      },
      // NO minzoom - en OpenLayers se ve a todos los niveles de zoom
    });
  }

  /**
   * Devuelve la expresión de icono según el tipo de marker
   * Según implementación original de OpenLayers
   */
  private getIconExpressionForType(type: string): any {
    switch (type) {
      case 'luminarias':
        // En el mapa de luminarias específico: iconos .webp por estado
        // En el mapa general: pin fijo (pin-luminaria.png)
        const isMapaLuminarias =
          this.mapaService.nombreMap === 'MapaLuminariasComponent';

        if (isMapaLuminarias) {
          // Mapa de luminarias: iconos por estado
          return [
            'match',
            ['get', 'estado'],
            0,
            'luminaria-estado-0',
            1,
            'luminaria-estado-1',
            2,
            'luminaria-estado-2',
            3,
            'luminaria-estado-3',
            4,
            'luminaria-estado-4',
            5,
            'luminaria-estado-5',
            'luminaria-default-icon', // Default
          ];
        } else {
          // Mapa general: pin fijo
          return 'luminaria-default-icon';
        }

      case 'gateways':
        // Según OpenLayers, usa diferentes iconos según estadoSalud
        return [
          'match',
          ['get', 'estadoSalud'],
          'ok',
          'gateway-ok-icon',
          'warning',
          'gateway-warning-icon',
          'error',
          'gateway-error-icon',
          'gateway-default-icon', // Default
        ];

      case 'sirenas':
        // Según OpenLayers, usa diferentes iconos según estado
        return [
          'match',
          ['get', 'estado'],
          'Verde',
          'sirena-verde-icon',
          'Roja',
          'sirena-roja-icon',
          'Azul',
          'sirena-azul-icon',
          'sirena-icon', // Default
        ];

      default:
        // Para el resto, usar el mapeo simple
        return this.TYPE_TO_ICON_MAP[type] || 'pin-icon';
    }
  }

  /**
   * Genera expresión de icon-size dinámica basada en zoom
   * Replica el comportamiento de OpenLayers: scale = 0.1 / resolution
   * A zoom lejano → icono muy pequeño
   * A zoom cercano → icono tamaño base
   * En zoom <= 12 se ocultan iconos y se muestran círculos
   */
  private getIconSizeExpression(type: string): any {
    // IMPORTANTE: En el mapa GENERAL, todos los iconos tienen tamaño FIJO
    // La escala dinámica con zoom es SOLO para mapas específicos (ej: mapa de luminarias)
    // En OpenLayers original, el mapa general usa:
    // - luminariaVectorLayer() con height: 35, width: 35 (fijo) → pin-luminaria.png 165x165
    // - sirenaVectorLayer() con height: 35, width: 35 (fijo)
    // - vehiculoVectorLayer() con height: 18, width: 18 (fijo)
    // - etc.

    // Tamaños por tipo (icon-size = tamaño_final_OpenLayers / tamaño_natural_PNG)
    const baseSizes: Record<string, number> = {
      vehiculos: 0.2, // 18px final / 90px natural = 0.2
      colectivos: 0.05, // 40px final / 800px natural = 0.05
      trackers: 0.1, // 20px final / 200px natural = 0.1
      activos: 0.212, // 35px final / 165px natural = 0.212
      alarmas: 0.04375, // 35px final / 800px natural = 0.04375
      sirenas: 1.458, // 35px final / 24px natural = 1.458 (verde.png, roja.png, azul.png)
      gateways: 0.04375, // 35px final / 800px natural = 0.04375 (SVGs 800x800)
    };

    // Luminarias: tamaño depende del mapa
    if (type === 'luminarias') {
      const isMapaLuminarias =
        this.mapaService.nombreMap === 'MapaLuminariasComponent';
      return isMapaLuminarias
        ? 0.1 // Mapa de luminarias: .webp ~350-500px con scale=0.1
        : 0.212; // Mapa general: pin-luminaria.png 165x165
    }

    // Retornar tamaño fijo para todos los tipos
    return baseSizes[type] || 0.875;
  }

  private addMarkerLayer(map: mapboxgl.Map, type: string, iconName: string) {
    const sourceId = (this.mapboxService.SOURCE_IDS as any)[type];
    const layerId = (this.mapboxService.LAYER_IDS as any)[type];

    // Obtener configuración del layer desde MapboxLayersService si existe
    const layerStyle = MapboxLayersService.LAYER_STYLES[type];

    if (layerStyle) {
      // Usar configuración completa del servicio (respeta icon-size configurado)
      map.addLayer({
        id: layerId,
        ...layerStyle,
        source: sourceId,
      } as any);
    } else {
      // Fallback con valores por defecto si no hay configuración
      map.addLayer({
        id: layerId,
        type: 'symbol',
        source: sourceId,
        layout: {
          'icon-image': iconName,
          'icon-size': 0.5,
          'icon-allow-overlap': true,
        },
      });
    }
  }

  private setupEventHandlers(map: mapboxgl.Map) {
    // Lista de layers clickeables
    const clickableLayers = [
      'vehiculos-unclustered',
      'colectivos-unclustered',
      'trackers-unclustered',
      'activos-unclustered',
      'luminarias-unclustered',
      'alarmas-unclustered',
      'sirenas-unclustered',
      'gateways-layer',
      'emergenciasMedicas-layer',
      'ambulancias-layer',
      'hospitales-layer',
      'centrosMedicos-layer',
      'paradas-layer',
      'marker-layer',
    ];

    // Click handlers para markers
    clickableLayers.forEach((layerId) => {
      map.on('click', layerId, async (e) => {
        if (!e.features || e.features.length === 0) return;

        // Si setMarker está activo, el click en el marker-layer no debe abrir popup
        if (this.mapaService.setMarker && layerId === 'marker-layer') return;

        const feature = e.features[0];
        const properties = feature.properties;

        // Extraer datos del feature (compatible con OpenLayers)
        // En Mapbox, properties pueden venir como objetos o strings JSON
        let markerData = properties?.['data'];

        // Si es un string JSON, parsearlo
        if (typeof markerData === 'string') {
          try {
            markerData = JSON.parse(markerData);
          } catch (e) {
            console.warn('⚠️ Error parseando data del marker:', e);
            markerData = null;
          }
        }

        const data = {
          tipo:
            properties?.['type'] ||
            layerId.replace('-unclustered', '').replace('-layer', ''),
          data: markerData,
          id: properties?.['id'],
          feature: feature,
        };

        // Obtener coordenadas
        if (feature.geometry.type === 'Point') {
          const coordinates = (feature.geometry as any).coordinates.slice();

          // Si los popups están deshabilitados (ej: map-card), no mostrar popup
          if (this.mapaService.popupsDisabled) {
            return;
          }

          // Agregar coordenadas al objeto data para compatibilidad con componentes
          const dataWithCoords = {
            ...data,
            coordinates: coordinates as [number, number],
          };

          // Si ya tiene datos completos, mostrar popup directamente
          if (data.data) {
            this.mostrarPopup(coordinates, dataWithCoords);
            this.mapaService.updatePopupData(dataWithCoords);
          }
          // Si no tiene datos pero tiene ID, cargar datos y mostrar popup
          else if (data.id) {
            // TODO: Implementar carga async de datos si es necesario
            // const dataBuscada = await this.dataEntidadService.obtenerEntidadPorId(data.tipo, data.id);
            // data.data = dataBuscada;
            this.mostrarPopup(coordinates, dataWithCoords);
            this.mapaService.updatePopupData(dataWithCoords);
          }
          // Fallback: mostrar lo que tengamos
          else {
            this.mostrarPopup(coordinates, dataWithCoords);
            this.mapaService.updatePopupData(dataWithCoords);
          }
        }
      });

      // Change cursor on hover
      map.on('mouseenter', layerId, () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', layerId, () => {
        map.getCanvas().style.cursor = '';
      });
    });

    // Click handlers para clusters (expand al hacer click)
    const clusterLayers = [
      'vehiculos-cluster',
      'colectivos-cluster',
      'trackers-cluster',
      'activos-cluster',
      'luminarias-cluster',
      'alarmas-cluster',
      'sirenas-cluster',
    ];

    clusterLayers.forEach((layerId) => {
      map.on('click', layerId, (e) => {
        if (!e.features || e.features.length === 0) return;

        const feature = e.features[0];
        const clusterId = feature.properties?.['cluster_id'];
        const sourceId = layerId.replace('-cluster', '-source');

        const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource;

        if (source && clusterId !== undefined) {
          source.getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err || !zoom) return;

            const coordinates = (feature.geometry as any).coordinates;
            map.easeTo({
              center: coordinates,
              zoom: zoom,
            });
          });
        }
      });

      // Change cursor on hover
      map.on('mouseenter', layerId, () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseleave', layerId, () => {
        map.getCanvas().style.cursor = '';
      });
    });

    // Canvas click para colocar marcador (cuando setMarker está activo)
    if (this.mapaService.setMarker) {
      map.on('click', (e) => {
        const coord: [number, number] = [e.lngLat.lng, e.lngLat.lat];
        this.mapaService.addMarker(coord);
      });
    }
  }

  /**
   * Normaliza el tipo de entidad de plural a singular para el popup
   * El template del popup espera tipos en singular (alarma, luminaria, etc)
   * pero los layers de Mapbox usan plural (alarmas, luminarias, etc)
   */
  private normalizarTipoParaPopup(tipo: string): TipoDatosPopup {
    const pluralToSingular: Record<string, TipoDatosPopup> = {
      alarmas: 'alarma',
      luminarias: 'luminaria',
      vehiculos: 'vehiculo',
      colectivos: 'colectivo',
      trackers: 'tracker',
      activos: 'activo',
      sirenas: 'sirena',
      gateways: 'gateway',
      emergenciasMedicas: 'emergenciaMedica',
      ambulancias: 'ambulancia',
      hospitales: 'hospital',
      centrosMedicos: 'centroMedico',
      paradas: 'parada',
    };

    return pluralToSingular[tipo] || (tipo as TipoDatosPopup);
  }

  /**
   * Muestra el popup genérico en las coordenadas especificadas
   * @param coordinate - Coordenadas [lng, lat]
   * @param data - Datos a mostrar en el popup
   */
  private mostrarPopup(coordinate: number[], data: any): void {
    // Cerrar popup anterior si existe
    this.cerrarPopup();

    const map = this.mapaService.getMap();
    if (!map) {
      console.warn('⚠️ Mapa no disponible');
      return;
    }

    // Normalizar tipo de plural a singular para el template del popup
    const tipoNormalizado = this.normalizarTipoParaPopup(data.tipo);

    // Actualizar propiedades del popup en el servicio
    this.mapaService.popupDatos = data;
    this.mapaService.popupTitulo = tipoNormalizado;
    this.mapaService.popupCoordinate = coordinate;
    this.mapaService.popupVisible = true;

    // Esperar a que Angular renderice el componente
    setTimeout(() => {
      this.crearOverlayPopup(coordinate, map);
    }, 0);
  }

  /**
   * Crea y posiciona el popup overlay en el mapa
   * Adaptado para Mapbox usando mapboxgl.Marker
   * @param coordinate - Coordenadas [lng, lat]
   * @param map - Instancia del mapa de Mapbox
   */
  private crearOverlayPopup(coordinate: number[], map: mapboxgl.Map): void {
    // Usar getElementById para obtener el contenedor del popup
    const element = document.getElementById('mapa-facil-popup-container');

    if (!element) {
      console.warn('⚠️ Contenedor del popup no encontrado en el DOM');
      return;
    }

    // Inicializar o actualizar el popup overlay en el servicio
    this.mapaService.initPopupOverlay(element);

    // Posicionar el popup
    this.mapaService.showPopup(coordinate);
  }

  private updateMapData(data: MapaFacilData) {
    // Actualizar datos y flags de visibilidad
    this.data = data;
    this.tipoMapaBase = this.mapaService.tipoMapaBase;
    this.updateVisibilityFlags(data);

    if (!this.map) return;

    // Update markers
    if (data.markers) {
      this.mapboxService.updateMarkers(data.markers);
      this.updateMarkerVisibility();

      // Centrar en el primer marker que tenga centrar: true
      const markerParaCentrar = data.markers.find((m) => m.centrar);
      if (markerParaCentrar) {
        this.map.flyTo({
          center: markerParaCentrar.coor,
          zoom: data.zoom || this.map.getZoom(),
          duration: 1000,
        });
      }
    }

    // Update polylines
    if (data.polylines) {
      this.stopAnimation();
      this.mapboxService.updatePolylines(data.polylines);
    }

    // Update polygons
    if (data.polygons) {
      this.mapboxService.updatePolygons(data.polygons);
    }

    // Update circles (modo lectura)
    if (data.circles) {
      this.mapboxService.updateCircles(data.circles);
    }

    // Update heatmaps
    if (data.heatmaps) {
      this.mapboxService.updateHeatmaps(data.heatmaps);
    }

    // Update center (solo si no hay marker con centrar:true)
    if (data.center && !data.markers?.find((m) => m.centrar)) {
      this.map.setCenter(data.center as [number, number]);
    }

    // Update zoom (solo si no hay marker con centrar:true)
    if (data.zoom && !data.markers?.find((m) => m.centrar)) {
      this.map.setZoom(data.zoom);
    }
  }

  public cambioDeMapa(tipo: TipoMapaBase): void {
    this.tipoMapaBase = tipo;
    if (!this.map) return;

    const styleUrl = this.getMapboxStyleUrl(tipo);
    this.map.setStyle(styleUrl);

    // Re-initialize layers after style change
    this.map.once('styledata', async () => {
      await this.onMapLoaded(this.map!);
    });
  }

  private getMapboxStyleUrl(tipo: TipoMapaBase): string {
    const styles: Record<TipoMapaBase, string> = {
      osm: 'mapbox://styles/mapbox/standard',
      satelite: 'mapbox://styles/mapbox/standard-satellite',
      cartodb: this.helper.mapaOscuroEnabled
        ? 'mapbox://styles/mapbox/dark-v11'
        : 'mapbox://styles/mapbox/light-v11',
      google: 'mapbox://styles/mapbox/standard', // Mapbox Standard como alternativa
    };
    return styles[tipo] || styles['osm'];
  }

  /**
   * Configura el ciclo día/noche para el estilo Mapbox Standard
   * Basado en la hora local, establece el preset de iluminación
   */
  private configurarCicloDiaNoche(map: mapboxgl.Map): void {
    // Solo aplicar si el estilo actual es Standard
    const isStandardStyle =
      this.tipoMapaBase === 'osm' || this.tipoMapaBase === 'google';

    const horaActual = new Date().getHours();

    if (!isStandardStyle) {
      return;
    }

    try {
      // Configurar 3D objects según preferencia del usuario
      const show3dObjects = this.helper.mapa3DEnabled;
      map.setConfigProperty('basemap', 'show3dObjects', show3dObjects);

      // Configurar lightPreset solo si está habilitado el ciclo día/noche
      if (this.helper.mapaDiaNocheEnabled) {
        const lightPreset = this.calcularLightPreset();
        map.setConfigProperty('basemap', 'lightPreset', lightPreset);
      } else {
        // Si está deshabilitado, usar 'day' por defecto
        map.setConfigProperty('basemap', 'lightPreset', 'day');
      }
    } catch (error) {
      console.error('❌ Error configurando Mapbox Standard:', error);
    }
  }

  /**
   * Calcula el preset de iluminación basado en la hora del día
   * @returns 'dawn' | 'day' | 'dusk' | 'night'
   */
  private calcularLightPreset(): 'dawn' | 'day' | 'dusk' | 'night' {
    const hora = new Date().getHours();

    // Amanecer: 5:00 - 7:59
    if (hora >= 5 && hora < 8) {
      return 'dawn';
    }
    // Día: 8:00 - 17:59
    else if (hora >= 8 && hora < 18) {
      return 'day';
    }
    // Atardecer: 18:00 - 20:59
    else if (hora >= 18 && hora < 21) {
      return 'dusk';
    }
    // Noche: 21:00 - 4:59
    else {
      return 'night';
    }
  }

  private loadMapCenter(): [number, number] {
    const savedLng = localStorage.getItem(
      `mapLng${this.mapaService.nombreMap}`,
    );
    const savedLat = localStorage.getItem(
      `mapLat${this.mapaService.nombreMap}`,
    );

    if (savedLng && savedLat) {
      return [parseFloat(savedLng), parseFloat(savedLat)];
    }

    // Default to Buenos Aires
    return [-58.443832, -34.587386];
  }

  private loadMapZoom(): number {
    const savedZoom = localStorage.getItem(
      `mapZoom${this.mapaService.nombreMap}`,
    );
    return savedZoom ? parseFloat(savedZoom) : 15;
  }

  private saveMapState(): void {
    if (!this.map || !this.mapaService.nombreMap) return;

    // Debounce para evitar guardar demasiado frecuentemente
    if (this.mapStateTimer) {
      clearTimeout(this.mapStateTimer);
    }

    this.mapStateTimer = setTimeout(() => {
      const center = this.map!.getCenter();
      const zoom = this.map!.getZoom();

      localStorage.setItem(
        `mapLng${this.mapaService.nombreMap}`,
        center.lng.toString(),
      );
      localStorage.setItem(
        `mapLat${this.mapaService.nombreMap}`,
        center.lat.toString(),
      );
      localStorage.setItem(
        `mapZoom${this.mapaService.nombreMap}`,
        zoom.toString(),
      );
    }, 500);
  }

  private stopAnimation() {
    this.animating = false;
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = undefined;
    }
  }

  /**
   * Cierra el popup flotante
   */
  public cerrarPopup(): void {
    this.mapaService.popupVisible = false;
    this.mapaService.popupDatos = null;
    this.mapaService.popupTitulo = undefined;
    this.mapaService.popupCoordinate = null;
    this.mapaService.updatePopupData({});

    // Ocultar el popup de Mapbox
    this.mapaService.hidePopup();

    if (this.popupTimeout) {
      clearTimeout(this.popupTimeout);
      this.popupTimeout = undefined;
    }

    this.cdr.detectChanges();
  }

  public clearSearch(): void {
    this.mapaService.clearAddressSearch();
  }

  public selectPrediction(pred: string): void {
    this.mapaService.selectPrediction(pred);
  }

  public async calcularDireccion(): Promise<void> {
    const coord = this.mapaService.markerCoord;
    if (!coord) return;
    await this.mapaService.calcularDireccionDesdeCoords(coord);
  }

  public onInputFocus(): void {
    if (
      this.mapaService.predicciones &&
      this.mapaService.predicciones.length > 0
    ) {
      this.mapaService.showSuggestions = true;
    }
  }

  public onInputBlur(): void {
    setTimeout(() => {
      this.mapaService.showSuggestions = false;
    }, 200);
  }

  public onInputChange(): void {
    if (
      !this.mapaService.direccionElegida ||
      this.mapaService.direccionElegida.length < 3
    ) {
      this.mapaService.predicciones = undefined;
      this.mapaService.showSuggestions = false;
    }
  }

  private setupAddressSearchListener(): void {
    if (!this.inputDireccion?.nativeElement) return;

    fromEvent<Event>(this.inputDireccion.nativeElement, 'keyup')
      .pipe(
        map((e) => (e.target as HTMLInputElement).value),
        filter((value) => !this.mapaService.loading && value.length > 2),
        debounceTime(700),
        distinctUntilChanged(),
      )
      .subscribe(async (value) => {
        if (value.length > 2) {
          await this.mapaService.buscarDireccion();
        } else {
          this.mapaService.predicciones = undefined;
          this.mapaService.showSuggestions = false;
        }
      });
  }

  // ============ MÉTODOS DE EDICIÓN ============

  public exportGeometries(): void {
    const geojson = this.mapboxDrawService.getEditedGeometries();

    // Crear blob y descargar
    const blob = new Blob([JSON.stringify(geojson, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `geometrias_${Date.now()}.geojson`;
    link.click();
    URL.revokeObjectURL(url);
  }

  // ============ MÉTODOS ESPECÍFICOS DEL POLYGON-EDITOR ============

  public async toggleDibujar(): Promise<void> {
    if (
      this.mapaService.drawingState.dibujando &&
      !this.mapaService.drawingState.dibujandoHueco
    ) {
      await this.mapboxDrawService.parar();
      this.mapaService.drawingState.dibujando = false;
    } else {
      await this.mapboxDrawService.dibujar(
        this.mapaService.editingConfig.drawingTool,
      );
      this.mapaService.drawingState.dibujando = true;
    }
  }

  public async toggleDibujarHueco(): Promise<void> {
    // TODO: Implementar dibujo de huecos con Mapbox Draw
  }

  public borrarTodo(): void {
    this.mapboxDrawService.borrarTodo();
  }

  /**
   * Activa el modo de edición de vértices (direct_select)
   * Permite arrastrar y mover vértices individuales
   */
  public continuar(): void {
    this.mapaService.continuar();
  }

  public onGeometryTypeChange(): void {
    // Actualizar estilo cuando cambia el tipo
    // TODO: Implement if needed
  }

  public getTipoNombre(): string {
    const tipo = this.mapaService.tiposGeometria.find(
      (t) => t.id === this.mapaService.editingConfig.drawingTool,
    );
    return tipo?.nombre || '';
  }

  public getTipoIcono(): string {
    const tipo = this.mapaService.tiposGeometria.find(
      (t) => t.id === this.mapaService.editingConfig.drawingTool,
    );
    return tipo?.icono || 'help';
  }

  public setCustomColor(color: string): void {
    this.mapaService.editingConfig.customColor = color;
    // TODO: Update drawing style with new color
  }

  // ============ MÉTODOS PARA ALTERNAR VISIBILIDAD DE MARKERS ============

  public toggleVehiculos(): void {
    this.vehiculosHabilitados = !this.vehiculosHabilitados;
    this.updateMarkerVisibility();
  }

  public toggleColectivos(): void {
    this.colectivosHabilitados = !this.colectivosHabilitados;
    this.updateMarkerVisibility();
  }

  public toggleTrackers(): void {
    this.trackersHabilitados = !this.trackersHabilitados;
    this.updateMarkerVisibility();
  }

  public toggleActivos(): void {
    this.activosHabilitados = !this.activosHabilitados;
    this.updateMarkerVisibility();
  }

  public toggleLuminarias(): void {
    this.luminariasHabilitadas = !this.luminariasHabilitadas;
    this.updateMarkerVisibility();
  }

  public toggleGateways(): void {
    this.gatewaysHabilitados = !this.gatewaysHabilitados;
    this.updateMarkerVisibility();
  }

  public toggleAlarmas(): void {
    this.alarmasHabilitadas = !this.alarmasHabilitadas;
    this.updateMarkerVisibility();
  }

  public toggleSirenas(): void {
    this.sirenasHabilitadas = !this.sirenasHabilitadas;
    this.updateMarkerVisibility();
  }

  public toggleTripPlayPause(): void {
    if (this.tripPaused) {
      this.mapaService.resumeTripAnimation();
    } else {
      this.mapaService.pauseTripAnimation();
    }
  }

  private updateMarkerVisibility(): void {
    if (!this.map) return;

    // Lógica de visibilidad:
    // 1. Si el tipo está deshabilitado (toggle OFF) → ocultar TODO
    // 2. Si el tipo está habilitado:
    //    a. Si clustering ON para ese tipo → mostrar clusters, ocultar unclustered/circle-far
    //    b. Si clustering OFF para ese tipo → ocultar clusters, mostrar unclustered/circle-far

    const visibilityMap = [
      {
        type: 'vehiculos',
        enabled: this.vehiculosHabilitados,
        clusterEnabled: this.vehiculosClusterEnabled,
      },
      {
        type: 'colectivos',
        enabled: this.colectivosHabilitados,
        clusterEnabled: this.colectivosClusterEnabled,
      },
      {
        type: 'trackers',
        enabled: this.trackersHabilitados,
        clusterEnabled: this.trackersClusterEnabled,
      },
      {
        type: 'activos',
        enabled: this.activosHabilitados,
        clusterEnabled: this.activosClusterEnabled,
      },
      {
        type: 'luminarias',
        enabled: this.luminariasHabilitadas,
        clusterEnabled: this.luminariasClusterEnabled,
      },
      {
        type: 'alarmas',
        enabled: this.alarmasHabilitadas,
        clusterEnabled: this.alarmasClusterEnabled,
      },
      {
        type: 'sirenas',
        enabled: this.sirenasHabilitadas,
        clusterEnabled: this.sirenasClusterEnabled,
      },
      {
        type: 'gateways',
        enabled: this.gatewaysHabilitados,
        clusterEnabled: false,
      }, // Gateways no tienen clustering
    ];

    visibilityMap.forEach(({ type, enabled, clusterEnabled }) => {
      // Layers involucrados (sistema de DOS CAPAS)
      const backgroundLayer = `${type}-background`; // Background (circle/compass)
      const unclusteredLayer = `${type}-unclustered`; // Foreground (icon)
      const circleFarLayer = `${type}-circle-far`;
      const clusterLayer = `${type}-cluster`;
      const clusterCountLayer = `${type}-cluster-count`;

      if (!enabled) {
        // Tipo deshabilitado → ocultar TODO
        if (this.map!.getLayer(backgroundLayer)) {
          this.map!.setLayoutProperty(backgroundLayer, 'visibility', 'none');
        }
        if (this.map!.getLayer(unclusteredLayer)) {
          this.map!.setLayoutProperty(unclusteredLayer, 'visibility', 'none');
        }
        if (this.map!.getLayer(circleFarLayer)) {
          this.map!.setLayoutProperty(circleFarLayer, 'visibility', 'none');
        }
        if (this.map!.getLayer(clusterLayer)) {
          this.map!.setLayoutProperty(clusterLayer, 'visibility', 'none');
        }
        if (this.map!.getLayer(clusterCountLayer)) {
          this.map!.setLayoutProperty(clusterCountLayer, 'visibility', 'none');
        }
      } else {
        // Tipo habilitado
        if (clusterEnabled) {
          // Clustering ON → mostrar clusters, ocultar unclustered/circle-far/background
          if (this.map!.getLayer(clusterLayer)) {
            this.map!.setLayoutProperty(clusterLayer, 'visibility', 'visible');
          }
          if (this.map!.getLayer(clusterCountLayer)) {
            this.map!.setLayoutProperty(
              clusterCountLayer,
              'visibility',
              'visible',
            );
          }
          if (this.map!.getLayer(backgroundLayer)) {
            this.map!.setLayoutProperty(backgroundLayer, 'visibility', 'none');
          }
          if (this.map!.getLayer(unclusteredLayer)) {
            this.map!.setLayoutProperty(unclusteredLayer, 'visibility', 'none');
          }
          if (this.map!.getLayer(circleFarLayer)) {
            this.map!.setLayoutProperty(circleFarLayer, 'visibility', 'none');
          }
        } else {
          // Clustering OFF → ocultar clusters, mostrar unclustered/circle-far/background
          if (this.map!.getLayer(clusterLayer)) {
            this.map!.setLayoutProperty(clusterLayer, 'visibility', 'none');
          }
          if (this.map!.getLayer(clusterCountLayer)) {
            this.map!.setLayoutProperty(
              clusterCountLayer,
              'visibility',
              'none',
            );
          }
          if (this.map!.getLayer(backgroundLayer)) {
            this.map!.setLayoutProperty(
              backgroundLayer,
              'visibility',
              'visible',
            );
          }
          if (this.map!.getLayer(unclusteredLayer)) {
            this.map!.setLayoutProperty(
              unclusteredLayer,
              'visibility',
              'visible',
            );
          }
          if (this.map!.getLayer(circleFarLayer)) {
            this.map!.setLayoutProperty(
              circleFarLayer,
              'visibility',
              'visible',
            );
          }
        }
      }
    });
  }

  // ============ MÉTODOS PARA ALTERNAR CLUSTERING ============

  public toggleVehiculosCluster(): void {
    this.vehiculosClusterEnabled = !this.vehiculosClusterEnabled;
    this.updateClusteringConfigOptimized();
    this.cdr.detectChanges();
  }

  public toggleColectivosCluster(): void {
    this.colectivosClusterEnabled = !this.colectivosClusterEnabled;
    this.updateClusteringConfigOptimized();
    this.cdr.detectChanges();
  }

  public toggleTrackersCluster(): void {
    this.trackersClusterEnabled = !this.trackersClusterEnabled;
    this.updateClusteringConfigOptimized();
    this.cdr.detectChanges();
  }

  public toggleActivosCluster(): void {
    this.activosClusterEnabled = !this.activosClusterEnabled;
    this.updateClusteringConfigOptimized();
    this.cdr.detectChanges();
  }

  public toggleLuminariasCluster(): void {
    this.luminariasClusterEnabled = !this.luminariasClusterEnabled;
    this.updateClusteringConfigOptimized();
    this.cdr.detectChanges();
  }

  public toggleAlarmasCluster(): void {
    this.alarmasClusterEnabled = !this.alarmasClusterEnabled;
    this.updateClusteringConfigOptimized();
    this.cdr.detectChanges();
  }

  public toggleSirenasCluster(): void {
    this.sirenasClusterEnabled = !this.sirenasClusterEnabled;
    this.updateClusteringConfigOptimized();
    this.cdr.detectChanges();
  }

  private updateClusteringConfigOptimized(): void {
    // Limpiar timer anterior
    if (this.clusterUpdateTimer) {
      clearTimeout(this.clusterUpdateTimer);
    }

    // Usar debounce para evitar updates muy frecuentes
    this.clusterUpdateTimer = setTimeout(() => {
      const clusterConfig = {
        enabled: true,
        vehiculos: { enabled: this.vehiculosClusterEnabled },
        colectivos: { enabled: this.colectivosClusterEnabled },
        trackers: { enabled: this.trackersClusterEnabled },
        activos: { enabled: this.activosClusterEnabled },
        luminarias: { enabled: this.luminariasClusterEnabled },
        alarmas: { enabled: this.alarmasClusterEnabled },
        sirenas: { enabled: this.sirenasClusterEnabled },
      };

      this.mapaService.updateClusteringConfig(clusterConfig);
      this.updateMarkerVisibility();
    }, 150);
  }
}
